import {
  conversationSteps, buildSessionEndedMessage, withBrochureButton, withBrochureIntro,
} from '../utils/conversationFlow';
import { saveConversationToStorage } from '../utils/storage';
import {
  completeAirtableSessionWithRetry,
  buildBrochureUrl,
  describeMissingBrochureLink,
} from '../utils/helpers';
import { replacePlaceholders, nextMessageId } from '../utils/aiResponseProcessor';

const RESTART_DELAY = 100;

const useSessionManagement = ({
  state,
  dispatch,
  setScrollPosition,
  onAmenitySelected,
  config,
  urlSlug,
}) => {
  const handleSessionEnd = () => {
    const goodbye = buildSessionEndedMessage(config);
    const endMessages = [
      { id: nextMessageId(), type: 'user', text: 'No' },
      goodbye,
    ];

    dispatch({ type: 'END_SESSION', payload: endMessages });

    // The goodbye goes up immediately; the brochure sentence, link field and
    // button are added to it when completion returns. The prospect's contact
    // hash is computed server-side and is the only part of
    // /<slug>/<hash> the browser cannot know, so the link cannot be
    // built until then — and waiting for the round trip before saying goodbye
    // would stall the last thing they see on a request that is allowed to fail.
    completeAirtableSessionWithRetry(state.sessionId, 'natural')
      .then((result) => {
        const brochureUrl = buildBrochureUrl(result?.contactHash, urlSlug);
        if (!brochureUrl) {
          console.warn(
            `[TourGuideAi] Goodbye sent without a brochure link for session ${state.sessionId}:`,
            describeMissingBrochureLink(result, urlSlug),
          );
          return;
        }

        dispatch({
          type: 'UPDATE_MESSAGE_BY_ID',
          payload: {
            id: goodbye.id,
            changes: {
              text: withBrochureIntro(goodbye.text),
              brochureUrl,
              buttons: withBrochureButton(goodbye.buttons, brochureUrl),
              instructions: null,
            },
          },
        });
      })
      // Nothing above should throw, but an unhandled rejection here would drop
      // the link with no trace at all.
      .catch((error) => {
        console.error('[TourGuideAi] Failed to attach the brochure link:', error);
      });

    if (onAmenitySelected) {
      onAmenitySelected(null);
    }
  };

  const handleRestartSession = () => {
    localStorage.removeItem(`chatbot_conversation_${state.sessionId}`);

    dispatch({ type: 'RESET_SESSION', payload: {} });
    setScrollPosition(0);

    if (onAmenitySelected) {
      onAmenitySelected(null);
    }

    const inputElement = document.querySelector('[data-tgai-input] textarea');
    if (inputElement) {
      inputElement.value = '';
    }

    setTimeout(() => {
      const welcomeStep = conversationSteps.welcome_form;
      if (welcomeStep) {
        const initialMessage = {
          id: nextMessageId(),
          type: 'bot',
          text: replacePlaceholders(welcomeStep.message, { selectedInterests: [], userInfo: {} }, config),
          instructions: welcomeStep.instructions || null,
          formFields: welcomeStep.formFields || null,
          submitButton: welcomeStep.submitButton || null,
          clearDataLink: welcomeStep.clearDataLink || null,
          stepType: welcomeStep.type,
        };

        dispatch({ type: 'SET_MESSAGES', payload: [initialMessage] });
        saveConversationToStorage(
          state.sessionId,
          [initialMessage],
          {
            currentStep: 'welcome_form',
            selectedInterests: [],
            selectedAmenities: [],
            visitedAmenities: [],
            userInfo: {
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              previousSession: false,
            },
            conversationData: {},
            formData: {},
            formErrors: {},
          },
          {},
          [],
        );
      }
    }, RESTART_DELAY);
  };

  return { handleSessionEnd, handleRestartSession };
};

export default useSessionManagement;
